// apps/web/src/api/client.ts
// HTTP 请求封装 — 自动注入 JWT，统一错误处理

import ky from "ky";
import { useAuthStore } from "@/stores/authStore";
import type { RefreshResponse } from "@examfast/shared-types";

// 生产环境使用 VITE_API_BASE_URL，开发环境走 Vite proxy
const BASE_URL =
  import.meta.env.VITE_API_BASE_URL && import.meta.env.PROD
    ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
    : "/api/v1";

// 标记是否已经在跳转中，避免重复跳转
let isRedirecting = false;
let refreshPromise: Promise<boolean> | null = null;

// 创建 ky 实例
// throwHttpErrors: false → 不自动抛异常，让 wrapper 统一处理 { success, data/error }
const apiClient = ky.create({
  prefixUrl: BASE_URL,
  timeout: 15000,
  throwHttpErrors: false, // 关键：禁用自动抛异常，由 parseResponse 处理
  hooks: {
    beforeRequest: [
      (request) => {
        const token = useAuthStore.getState().accessToken;
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      },
    ],
  },
});

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { message?: string; code?: string };
}

type ApiError = Error & { code?: string; status?: number };
type RequestFactory = () => Promise<Response>;

function createApiError(message: string, status: number, code?: string): ApiError {
  const error = new Error(message) as ApiError;
  error.code = code;
  error.status = status;
  return error;
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("json")) {
    throw createApiError(
      response.ok ? "服务器响应格式异常" : `请求失败 (${response.status})`,
      response.status
    );
  }

  let json: ApiEnvelope<T>;
  try {
    json = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw createApiError("服务器返回了无效响应", response.status);
  }

  if (!json || typeof json !== "object" || typeof json.success !== "boolean") {
    throw createApiError("服务器响应格式异常", response.status);
  }

  if (!response.ok || !json.success) {
    throw createApiError(
      json.error?.message ?? `请求失败 (${response.status})`,
      response.status,
      json.error?.code
    );
  }

  return json.data as T;
}

function isAuthEndpoint(url: string): boolean {
  const path = url.split("?", 1)[0].replace(/^\//, "");
  return path === "auth/login" || path === "auth/register" || path === "auth/refresh";
}

function redirectToLogin(): void {
  useAuthStore.getState().logout();
  if (!isRedirecting && !window.location.pathname.includes("/login")) {
    isRedirecting = true;
    window.location.href = "/login";
  }
}

async function refreshSession(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) return false;

    try {
      const response = await apiClient.post("auth/refresh", { json: { refreshToken } });
      const tokens = await parseResponse<RefreshResponse>(response);
      useAuthStore.getState().setTokens(tokens.accessToken, tokens.refreshToken);
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

async function request<T>(url: string, factory: RequestFactory): Promise<T> {
  const accessToken = useAuthStore.getState().accessToken;
  let response = await factory();

  if (response.status === 401 && !isAuthEndpoint(url)) {
    const tokenWasAlreadyRefreshed = useAuthStore.getState().accessToken !== accessToken;
    const refreshed = tokenWasAlreadyRefreshed || (await refreshSession());

    if (refreshed) {
      response = await factory();
    }

    if (!refreshed || response.status === 401) {
      redirectToLogin();
    }
  }

  return parseResponse<T>(response);
}

/*
 * 通用请求方法：自动解包 { success, data }，401 时刷新令牌并重试一次。
 */
export const api = {
  async get<T>(url: string): Promise<T> {
    return request<T>(url, () => apiClient.get(url));
  },

  async post<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, () => apiClient.post(url, { json: body }));
  },

  async put<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, () => apiClient.put(url, { json: body }));
  },

  async del<T>(url: string): Promise<T> {
    return request<T>(url, () => apiClient.delete(url));
  },

  async upload<T>(url: string, file: File): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);
    return request<T>(url, () => apiClient.post(url, { body: formData }));
  },
};

export default api;
